import React, { useState, useEffect, useRef } from 'react';
import { Typography, Radio, Table, Empty, Upload, Flex, Space, Button, Alert, Popover, Card } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import BotaoDownload from 'components/Botoes/BotaoDownload/BotaoDownload';
import { useAuth } from 'context/AuthContext';
import FormulariosEntidadeService from 'services/FormulariosEntidadeService';
import { ModalErro } from 'components/Modais/ModalErro';
import UsuariosService from 'services/UsuariosService';
import { ModalSucesso } from 'components/Modais/ModalSucesso';
import STEPS from '../Constants/steps';
import '../styles.css';
import { Link } from 'react-router-dom';
import { useRoteiro } from 'context/RoteiroContext';

const { Dragger } = Upload;
const { Title, Text } = Typography;

function NovaOperacao({stepAtual, setStepAtual, obterUltimoLog}) {
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [listaArquivos, setListaArquivos] = useState([]);
    const [delimitadorColuna, setDelimitadorColuna] = useState(';');

    const {setReferenciaObjetosVisuais} = useRoteiro();
    const refBotaoBaixarCsvApenasCabecalhos = useRef(null);
    const refBotaoBaixarCsvComDadosExemplo = useRef(null);
    const refBotaoExibirRegrasFormulario = useRef(null);
    const refBotaoProximaEtapa = useRef(null);
    const refs = [
        { id: 'refCriacaoMultiplosUsuariosBotaoBaixarCsvApenasCabecalhos', ref: refBotaoBaixarCsvApenasCabecalhos },
        { id: 'refCriacaoMultiplosUsuariosBotaoBaixarCsvComDadosExemplo', ref: refBotaoBaixarCsvComDadosExemplo },
        { id: 'refCriacaoMultiplosUsuariosBotaoExibirRegrasFormulario', ref: refBotaoExibirRegrasFormulario },
        { id: 'refCriacaoMultiplosUsuariosBotaoProximaEtapaBaixarModelo', ref: refBotaoProximaEtapa }
    ];

    const {direito} = useAuth();

    useEffect(()=>{
        inicializarPagina();
    }, []);

    const inicializarPagina = async () => {
        await mapearDadosTabelaFormulario();
        setReferenciaObjetosVisuais(refs);
    }

    const mapearDadosTabelaFormulario = async () => {
        const camposFormularioPortal = await FormulariosEntidadeService.ListarCamposPortalCliente();
        const dataSourceCampos = camposFormularioPortal.map(campoFormulario => {
            return {
                campo: campoFormulario.nome,
                obrigatorio: campoFormulario.obrigatorio ? 'Sim' : 'Não',
                tamanho: campoFormulario.campoPortalCliente.tamanho 
            };
        });
        setDataSource(dataSourceCampos);
    }
    
    const baixarCsvExemploCamposFormulario = async (incluirDadosDeExemplo) => {
        return await FormulariosEntidadeService.ObterLinkDownloadCsvCamposFormulario(delimitadorColuna, incluirDadosDeExemplo);
    }

    const colunasTabelaCamposFormulario = [
        {
            title: 'Campo',
            dataIndex: 'campo',
            key: 'campo'
        },
        {
            title: 'Obrigatório',
            dataIndex: 'obrigatorio',
            key: 'obrigatorio'
        },
        {
            title: 'Tamanho',
            dataIndex: 'tamanho',
            key: 'tamanho'
        }
    ];

    const tabelaComRegrasDoFormulario = (
        <Table
            dataSource={dataSource}
            columns={colunasTabelaCamposFormulario}
            pagination={false}
            size="small"
            bordered={false}
            locale={{
                emptyText: <Empty description="Nenhum campo encontrado" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            }}
        />
      );

    const propsDragger = {
        name: "file",
        multiple: false,
        fileList: listaArquivos,
        onChange(info) {
            setListaArquivos(info.fileList);
        },
        customRequest: async ({ file }) => {
            setLoading(true);

            const formData = new FormData();
            formData.append("CsvUsuarios", file);
            formData.append("tenantId", direito.axtTenantId);
            formData.append("direitoId", direito.entitlementId);
            formData.append("delimitadorColunas", delimitadorColuna);

            try {
                const resposta = await UsuariosService.EnviarPedidoCriacaoMultiplosUsuarios(formData);
                ModalSucesso('Sucesso no upload do arquivo!', resposta);

                await obterUltimoLog();
            } catch (error) {
                ModalErro('Erro ao enviar arquivo', error.response?.data?.erros ?? ['Erro inesperado ao fazer upload do arquivo.']);
            }

            setListaArquivos([]);
            setLoading(false);
        },
        showUploadList: true,
    };

    return (
        <Card>
            {stepAtual === STEPS.BAIXAR_MODELO && (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Text strong>
                        Selecione o delimitador de colunas e baixe o arquivo CSV para prosseguir:
                    </Text>

                    <Radio.Group
                        onChange={e => setDelimitadorColuna(e.target.value)}
                        value={delimitadorColuna}
                        options={[
                            { value: ';', label: 'Para configurações em português (;)'  },
                            { value: ',', label: 'Para configurações em outros idiomas (,)' }
                        ]}
                    />

                    <Flex className='big-gap'>
                        <BotaoDownload
                            apiCallback={() => baixarCsvExemploCamposFormulario(false)}
                            linkName="csvApenasCabecalhos"
                            message="Baixar CSV (apenas cabeçalhos)"
                            refReact={refBotaoBaixarCsvApenasCabecalhos}
                        />
                        <BotaoDownload
                            apiCallback={() => baixarCsvExemploCamposFormulario(true)}
                            linkName="csvDadosExemplo"
                            message="Baixar CSV com dados de exemplo"
                            refReact={refBotaoBaixarCsvComDadosExemplo}
                        />
                    </Flex>

                    <Alert
                        message={<Text strong>Atenção! O arquivo deve conter no máximo 300 usuários.</Text>}
                        description={
                            <Flex vertical>
                                <Text strong>Não remova o cabeçalho da planilha baixada. Sem ele, ocorrerá erro no upload.</Text>
                                <Text strong>Respeite os campos obrigatórios e limites de caracteres indicados no botão abaixo.</Text>
                                <Text strong>Todos os usuários criados receberão o mesmo plano de e-mail e o mesmo perfil de acesso (definidos na etapa de configuração).</Text>
                                <Text strong>Se precisar de mais de um plano, faça a criação em lotes separados (ex.: 20 Plano 1 e depois 30 Plano 2).</Text>
                            </Flex>
                        }
                        type="warning"
                        showIcon
                    />
                    
                    <Popover content={tabelaComRegrasDoFormulario} trigger="click">
                        <Button type="primary" ref={refBotaoExibirRegrasFormulario}>Exibir regras do formulário</Button>
                    </Popover>

                    <Flex justify="end" gap="small" className='flex-botoes-progresso'>
                        <Link to="/usuarios">
                            <Button>Sair</Button>
                        </Link>
                        <Button type="primary" onClick={() => setStepAtual(STEPS.UPLOAD_ARQUIVO)} disabled={loading} ref={refBotaoProximaEtapa}>
                            Próxima etapa
                        </Button>
                    </Flex>
                </Space>
            )}

            {stepAtual === STEPS.UPLOAD_ARQUIVO && (
                <Flex vertical>
                    <Title level={3} marginTop={0}>Upload do arquivo CSV</Title>
                    <Text style={{ marginBottom: 30 }}>
                        Após preencher o arquivo CSV com os usuários, envie-o abaixo:
                    </Text>

                    <Dragger {...propsDragger} disabled={loading}>
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Clique ou arraste o arquivo para fazer o upload</p>
                        <p className="ant-upload-hint">
                            Suporta apenas arquivos CSV com as mesmas colunas do arquivo de exemplo
                        </p>
                    </Dragger>

                    <Flex justify="flex-start" className='flex-botoes-progresso'>
                        <Button onClick={() => setStepAtual(STEPS.BAIXAR_MODELO)} disabled={loading}>Voltar</Button>
                    </Flex>
                </Flex>
            )}
        </Card>
    );
}

export default NovaOperacao;